import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';

import { getNftCountByTokenId } from 'services/hoard.service';

import { queryKeys } from './query-keys';

export interface UseNftCountByIdReturn {
  count: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch the number of NFT shares owned by the user for a given token ID
 *
 * @param tokenId - The token ID to query
 * @returns count, loading state, error state, and refetch function
 */
export const useNftCountById = (tokenId: string): UseNftCountByIdReturn => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);

  const {
    data: count = 0,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.assets.count(tokenId, walletAddress),
    queryFn: () => getNftCountByTokenId(tokenId),
    enabled: !!walletAddress && !!tokenId,
    // Return 0 if there's an error or no wallet
    retry: 1,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  return {
    count,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch NFT count') : null,
    refetch,
  };
};
